/**
 * ReportFilterDrawer — "Report Settings" drawer with accordion sections.
 *
 * Replaces V1's multi-tab modal with a modern accordion-based drawer (660px).
 * 6 accordion groups: Time Range + 5 filter groups (11 sub-sections).
 *
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onApply           — (filters) => void
 *   onClear           — () => void
 *   filterOptions     — all option datasets from fetchFilterOptions
 *   currentFilters    — current active filter selections
 *   saving            — boolean (loading state for apply button)
 *   timeRanges        — TIME_RANGES constant array
 *   rangeId           — currently selected range id
 *   customRange       — boolean: is custom range active?
 *   start             — Date object: start date
 *   end               — Date object: end date
 *   onTimeRangeChange — (range) => void
 *   onCustomToggle    — () => void
 *   onStartChange     — (date) => void
 *   onEndChange       — (date) => void
 *
 * Optional (each replaced a forked copy of this drawer — docs/DUPLICATION_AUDIT.md, B4):
 *   sections          — accordion groups; presets in components/v2/reportFilterSections.js
 *                       (default ALERT_REPORT_SECTIONS)
 *   hideResponseTime  — no Response Time control in the Events group
 *   hideTimeRange     — no Time Range group
 *   timeRangeDisabled — Time Range shown but locked ("Today (fixed)"), e.g. Missed
 *                       Check-in and Full Battery, which only report the current state
 *   dateOnly          — date pickers without a time
 *   size              — drawer width modifier ('md' default = 32rem, like the other
 *                       pages' drawers; 'lg' / 'xl' wider)
 *   defaultExpanded   — group ids open on first render (default ['timeRange'])
 *
 * `sections` and `defaultExpanded` must be stable (module constants): the working copy
 * resets when they change identity.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { describeTimeWindow } from '../../../../utils/reportTimeWindow.js';
import TimeWindowPicker from '../../../../components/v2/TimeWindowPicker.jsx';
import FilterSubSection from '../../../../components/v2/FilterSubSection.jsx';
import { ALERT_REPORT_SECTIONS } from '../../../../components/v2/reportFilterSections.js';

/* Filter keys held as plain objects, not Sets. Every place that converts between
   the parent's arrays and this drawer's Sets has to skip them — `new Set({...})`
   throws, so missing one crashes the drawer on open rather than degrading. Kept as
   a set so adding a third object-shaped filter cannot miss a site. */
const OBJECT_FILTER_KEYS = new Set(['responseTime']);
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Drawer from '../../../../components/v2/Drawer.jsx';

// ---------------------------------------------------------------------------
// Empty filter state factory
// ---------------------------------------------------------------------------

const DEFAULT_RESPONSE_TIME = { enabled: false, operator: 'lt', value: 60 };
const DEFAULT_EXPANDED = ['timeRange'];

/* One empty Set per sub-section key shown, plus any key the page already holds (so a
   Clear All followed by Apply never drops a key the page reads), plus responseTime
   when the Events group offers it. Derived rather than listed so a section preset
   with different keys (wellness, call logs...) needs no matching edit here. */
function emptyFilters(sections, withResponseTime, pageKeys = []) {
    const result = {};
    sections.forEach((section) => section.subs.forEach((sub) => { result[sub.key] = new Set(); }));
    pageKeys.forEach((k) => {
        if (k !== 'timeWindow' && !OBJECT_FILTER_KEYS.has(k) && !(k in result)) result[k] = new Set();
    });
    if (withResponseTime || pageKeys.includes('responseTime')) result.responseTime = { ...DEFAULT_RESPONSE_TIME };
    return result;
}

function cloneFilters(f) {
    const result = {};
    Object.keys(f).forEach((k) => {
        if (OBJECT_FILTER_KEYS.has(k)) {
            result[k] = { ...f[k] };
        } else {
            result[k] = new Set(f[k]);
        }
    });
    return result;
}

function filtersToSets(currentFilters, sections, withResponseTime) {
    if (!currentFilters) return emptyFilters(sections, withResponseTime);
    const result = {};
    Object.keys(currentFilters).forEach((k) => {
        if (k === 'responseTime') {
            result[k] = { ...(currentFilters[k] || DEFAULT_RESPONSE_TIME) };
        } else if (k === 'timeWindow') {
            /* Skipped on purpose: the page owns it and it commits immediately, so it
               must not ride the drawer's Apply — a stale copy taken when the drawer
               opened would overwrite a change made from the header popover. */
        } else {
            result[k] = new Set((currentFilters[k] || []).map(String));
        }
    });
    return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReportFilterDrawer = ({
    open,
    onClose,
    onApply,
    onClear,
    filterOptions,
    currentFilters,
    saving,
    // Time range props
    timeRanges,
    rangeId,
    customRange,
    start,
    end,
    onTimeRangeChange,
    onCustomToggle,
    onStartChange,
    onEndChange,
    // Optional overrides
    hideTimeRange = false,
    sections: sectionsProp,
    hideResponseTime = false,
    /* Opt-in, not opt-out like hideResponseTime above. This drawer is shared by four
       report pages and only the ones whose backend honours a time-of-day window
       should offer the control — a filter that silently does nothing is worse than
       an absent one. Flip it on per page as each report gains support. */
    showTimeWindow = false,
    timeWindow,
    onTimeWindowChange,
    dateOnly = false,
    timeRangeDisabled = false,
    size = 'md',
    defaultExpanded = DEFAULT_EXPANDED,
}) => {
    const activeSections = sectionsProp || ALERT_REPORT_SECTIONS;
    const offersResponseTime = !hideResponseTime && activeSections.some((sec) => sec.id === 'events');
    // Working copy of filters
    const [working, setWorking] = useState(() => filtersToSets(currentFilters, activeSections, offersResponseTime));
    const [expandedSections, setExpandedSections] = useState(() => new Set(defaultExpanded));

    // Sync working copy when drawer opens or currentFilters change
    useEffect(() => {
        if (open) {
            setWorking(filtersToSets(currentFilters, activeSections, offersResponseTime));
        }
    }, [open, currentFilters, activeSections, offersResponseTime]);

    // Auto-focus the custom start input when the drawer opens already in Custom
    // mode (e.g. user picked "Custom Range…" from the time popover).
    const customStartRef = useRef(null);
    const customFocusGuardRef = useRef(false);
    useEffect(() => {
        const shouldFocus = open && customRange && !timeRangeDisabled;
        if (shouldFocus && !customFocusGuardRef.current) {
            customFocusGuardRef.current = true;
            setExpandedSections((s) => (s.has('timeRange') ? s : new Set([...s, 'timeRange'])));
            const t = setTimeout(() => {
                try { customStartRef.current?.setFocus?.(); } catch { /* no-op */ }
            }, 80);
            return () => clearTimeout(t);
        }
        if (!shouldFocus) customFocusGuardRef.current = false;
        return undefined;
    }, [open, customRange, timeRangeDisabled]);

    // Toggle accordion
    const toggleSection = useCallback((sectionId) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            return next;
        });
    }, []);

    // Checkbox toggle
    const handleToggle = useCallback((filterKey, id) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            if (next[filterKey].has(id)) {
                next[filterKey].delete(id);
            } else {
                next[filterKey].add(id);
            }
            return next;
        });
    }, []);

    // Select all
    const handleSelectAll = useCallback((filterKey, allIds) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next[filterKey] = new Set(allIds);
            return next;
        });
    }, []);

    // Clear section
    const handleClear = useCallback((filterKey) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next[filterKey] = new Set();
            return next;
        });
    }, []);

    // Response time handlers
    const handleRespToggle = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, enabled: e.target.checked };
            return next;
        });
    }, []);

    const handleRespOperator = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, operator: e.target.value };
            return next;
        });
    }, []);

    const handleRespValue = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, value: parseInt(e.target.value, 10) || 0 };
            return next;
        });
    }, []);

    // Count selections for a section group
    const getSectionCount = useCallback((section) => {
        let count = 0;
        section.subs.forEach((sub) => {
            count += (working[sub.key]?.size || 0);
        });
        if (section.id === 'events' && offersResponseTime && working.responseTime?.enabled) {
            count += 1;
        }
        return count;
    }, [working, offersResponseTime]);

    // Apply filters
    const handleApply = useCallback(() => {
        // Convert Sets to arrays for the parent
        const result = {};
        Object.keys(working).forEach((k) => {
            if (OBJECT_FILTER_KEYS.has(k)) {
                result[k] = { ...working[k] };
            } else {
                result[k] = [...working[k]];
            }
        });
        onApply(result);
    }, [working, onApply]);

    // Clear all
    const handleClearAll = useCallback(() => {
        setWorking(emptyFilters(activeSections, offersResponseTime, Object.keys(currentFilters || {})));
        onClear();
    }, [onClear, activeSections, offersResponseTime, currentFilters]);

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Report Settings"
                size={size}
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} disabled={saving} type="button">
                            Clear All
                        </button>
                        <button className="dp-btn dp-btn--primary" onClick={handleApply} disabled={saving} type="button">
                            {saving ? 'Applying\u2026' : 'Apply Settings'}
                        </button>
                    </>
                )}
            >
                {/* Time Range — first accordion (changes take effect immediately) */}
                {!hideTimeRange ? (
                <div className={`ar-accordion${timeRangeDisabled ? ' ar-time-disabled' : ''}`}>
                    <button
                        className={`ar-accordion__header${expandedSections.has('timeRange') ? ' ar-accordion__header--open' : ''}`}
                        onClick={() => toggleSection('timeRange')}
                        type="button"
                    >
                        <span className="ar-accordion__icon">
                            {expandedSections.has('timeRange') ? '\u25BC' : '\u25B6'}
                        </span>
                        <span className="ar-accordion__label">Time Range</span>
                        <span className="ar-accordion__count">
                            {timeRangeDisabled
                                ? 'Today (fixed)'
                                : customRange
                                    ? 'Custom'
                                    : (timeRanges || []).find((r) => r.id === rangeId)?.label || 'Yesterday'}
                            {/* Appended so a window is visible with the section collapsed —
                                otherwise a report silently excluding half the day looks
                                identical to one that is not. */}
                            {describeTimeWindow(timeWindow?.start, timeWindow?.end)
                                ? ` · ${describeTimeWindow(timeWindow?.start, timeWindow?.end)}`
                                : ''}
                        </span>
                    </button>
                    {expandedSections.has('timeRange') ? (
                        <div className="ar-accordion__body">
                            <div className="ar-time-bar">
                                <div className="ar-time-bar__ranges">
                                    {(timeRanges || []).map((range) => (
                                        <button
                                            key={range.id}
                                            className={`ar-time-btn${rangeId === range.id && !customRange ? ' ar-time-btn--active' : ''}`}
                                            onClick={() => onTimeRangeChange(range)}
                                            type="button"
                                            disabled={timeRangeDisabled}
                                        >
                                            {range.label}
                                        </button>
                                    ))}
                                    <button
                                        className={`ar-time-btn${customRange ? ' ar-time-btn--active' : ''}`}
                                        onClick={onCustomToggle}
                                        type="button"
                                        disabled={timeRangeDisabled}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {customRange && !timeRangeDisabled ? (
                                    <div className="ar-custom-range">
                                        <DatePicker
                                            ref={customStartRef}
                                            selected={start}
                                            onChange={(date) => onStartChange(date)}
                                            selectsStart
                                            startDate={start}
                                            endDate={end}
                                            maxDate={end}
                                            {...(!dateOnly && { showTimeSelect: true, timeIntervals: 15 })}
                                            dateFormat={dateOnly ? 'MM/dd/yyyy' : 'MM/dd/yyyy h:mm aa'}
                                            className="ar-custom-range__input"
                                            placeholderText={dateOnly ? 'Start date' : 'Start date/time'}
                                        />
                                        <span className="ar-custom-range__sep">to</span>
                                        <DatePicker
                                            selected={end}
                                            onChange={(date) => onEndChange(date)}
                                            selectsEnd
                                            startDate={start}
                                            endDate={end}
                                            minDate={start}
                                            {...(!dateOnly && { showTimeSelect: true, timeIntervals: 15 })}
                                            dateFormat={dateOnly ? 'MM/dd/yyyy' : 'MM/dd/yyyy h:mm aa'}
                                            className="ar-custom-range__input"
                                            placeholderText={dateOnly ? 'End date' : 'End date/time'}
                                        />
                                    </div>
                                ) : null}
                                {/* Time of day sits with the date range because that is
                                    how it reads to a user — "last week, 8 AM to 8 PM" — even
                                    though it is stored with the filters. Like the range
                                    buttons above it, this commits immediately rather than on
                                    Apply. */}
                                {showTimeWindow ? (
                                    <div className="ar-filter-section">
                                        <div className="ar-filter-section__header">
                                            <span className="ar-filter-section__label">Time of Day</span>
                                        </div>
                                        <TimeWindowPicker
                                            value={timeWindow}
                                            onChange={onTimeWindowChange}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
                ) : null}

                {activeSections.map((section) => {
                    const isExpanded = expandedSections.has(section.id);
                    const count = getSectionCount(section);
                    return (
                        <div key={section.id} className="ar-accordion">
                            <button
                                className={`ar-accordion__header${isExpanded ? ' ar-accordion__header--open' : ''}`}
                                onClick={() => toggleSection(section.id)}
                                type="button"
                            >
                                <span className="ar-accordion__icon">
                                    {isExpanded ? '\u25BC' : '\u25B6'}
                                </span>
                                <span className="ar-accordion__label">{section.label}</span>
                                {count > 0 ? (
                                    <span className="ar-accordion__count">{count} selected</span>
                                ) : null}
                            </button>
                            {isExpanded ? (
                                <div className="ar-accordion__body">
                                    {/* Response Time — special section inside Events */}
                                    {section.id === 'events' && offersResponseTime ? (
                                        <div className="ar-filter-section">
                                            <div className="ar-filter-section__header">
                                                <span className="ar-filter-section__label">Response Time</span>
                                            </div>
                                            <div className="ar-resp-filter">
                                                <label className="ar-resp-filter__toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={working.responseTime?.enabled || false}
                                                        onChange={handleRespToggle}
                                                    />
                                                    <span>Enable</span>
                                                </label>
                                                {working.responseTime?.enabled ? (
                                                    <div className="ar-resp-filter__controls">
                                                        <select
                                                            className="ar-resp-filter__select"
                                                            value={working.responseTime?.operator || 'lt'}
                                                            onChange={handleRespOperator}
                                                        >
                                                            <option value="lt">Less than</option>
                                                            <option value="gt">Greater than</option>
                                                            <option value="eq">Equal to</option>
                                                        </select>
                                                        <input
                                                            className="ar-resp-filter__input"
                                                            type="number"
                                                            value={working.responseTime?.value || 60}
                                                            onChange={handleRespValue}
                                                            min={0}
                                                        />
                                                        <span className="ar-resp-filter__unit">min</span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    ) : null}
                                    {section.subs.map((sub) => (
                                        <FilterSubSection
                                            key={sub.key}
                                            subDef={sub}
                                            options={filterOptions?.[sub.optionsKey] || []}
                                            selected={working[sub.key] || new Set()}
                                            onToggle={handleToggle}
                                            onSelectAll={handleSelectAll}
                                            onClear={handleClear}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </Drawer>
        </>
    );
};

export default ReportFilterDrawer;
